import React, { useEffect, useMemo, useState } from "react"
import { Button, Divider, Flex, Input, Radio, Select, Spin, Tooltip } from "antd"
import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { cleanCancelRefund, fetchCancelRefundBreakdown, getCancelReasons, submitCancelAndRefund } from "store/bookings"
import { toaster } from "components/common/Toast"
import { Modal } from "ui/Modal"
import { Text, Title } from "ui/Typography"
import { numberToCurrency } from "helpers/string"
import styles from "./index.module.css"

const CancelBookingModal = ({ open, onCancel, onConfirm, bookings }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [refundType, setRefundType] = useState("full")
  const [partialAmount, setPartialAmount] = useState("")
  const [partialAmountError, setPartialAmountError] = useState("")
  const [selectedReason, setSelectedReason] = useState(null)
  const [reasonError, setReasonError] = useState("")
  const [otherReason, setOtherReason] = useState("")
  const [otherReasonError, setOtherReasonError] = useState("")

  const { data: breakdownData, loading: breakdownLoading } = useSelector(
    (state) => state.bookings.cancelRefundBreakdown || {}
  )
  const { loading: submitLoading } = useSelector((state) => state.bookings.cancelRefundSubmit || {})
  const { data: cancelReasons, loading: reasonsLoading, failed: reasonsFailed } = useSelector(
    (state) => state.bookings.cancelReasons || {}
  )

  const isOtherSelected = useMemo(
    () => (cancelReasons || []).find((r) => r.id === selectedReason)?.reason?.toLowerCase() === "other",
    [cancelReasons, selectedReason]
  )

  const booking = bookings?.[0]
  const bookingId = booking?.id

  useEffect(() => {
    dispatch(getCancelReasons())
    if (bookingId) {
      dispatch(fetchCancelRefundBreakdown(bookingId))
    }
    return () => {
      dispatch(cleanCancelRefund())
    }
  }, [dispatch, bookingId])

  const totalTaxes =
    (breakdownData?.tax_and_fee || 0) +
    (breakdownData?.custom_fees || []).reduce((sum, fee) => sum + (fee.price || 0), 0)

  const validatePartialAmount = (value) => {
    if (!value && value !== 0) return t("bookings.cancel_modal.amount_required")
    const num = Number.parseFloat(value)
    if (Number.isNaN(num) || num <= 0) return t("bookings.cancel_modal.amount_positive")
    if (num > (breakdownData?.amount_paid_by_guest || 0))
      return t("bookings.cancel_modal.amount_exceeds", { amount: numberToCurrency(breakdownData?.amount_paid_by_guest) })
    return ""
  }

  const handlePartialAmountChange = (e) => {
    const val = e.target.value
    setPartialAmount(val)
    if (partialAmountError) setPartialAmountError(validatePartialAmount(val))
  }

  const handleRadioChange = (e) => {
    setRefundType(e.target.value)
    setPartialAmount("")
    setPartialAmountError("")
  }

  const handleConfirmClick = async () => {
    if (!selectedReason) {
      setReasonError(t("bookings.cancel_modal.reason_required"))
      return
    }
    if (isOtherSelected && !otherReason.trim()) {
      setOtherReasonError(t("bookings.cancel_modal.other_reason_required"))
      return
    }
    if (refundType === "partial") {
      const error = validatePartialAmount(partialAmount)
      if (error) {
        setPartialAmountError(error)
        return
      }
    }
    try {
      const data = {
        cancel_reason_id: selectedReason,
        ...(isOtherSelected ? { other_reason: otherReason.trim() } : {}),
        refund_type: refundType,
        ...(refundType === "partial" ? { refund_amount: parseFloat(partialAmount) } : {})
      }
      const result = await dispatch(submitCancelAndRefund(booking?.uuid || bookingId, data))
      if (result?.message) toaster.success(result.message)
      onConfirm?.()
    } catch (e) {
      console.error("Refund submission error:", e)
    }
  }

  const taxesTooltipContent = (
    <Flex vertical gap={4}>
      {breakdownData?.tax_and_fee > 0 && (
        <Flex justify="space-between" gap={24}>
          <span>{t("bookings.cancel_modal.taxes_and_fees")}</span>
          <span>{numberToCurrency(breakdownData.tax_and_fee)}</span>
        </Flex>
      )}
      {(breakdownData?.custom_fees || []).map((fee) => (
        <Flex key={fee.id} justify="space-between" gap={24}>
          <span>{fee.title}</span>
          <span>{numberToCurrency(fee.price)}</span>
        </Flex>
      ))}
    </Flex>
  )

  let breakdownContent
  if (breakdownLoading) {
    breakdownContent = (
      <Flex justify="center" align="center" className={styles.breakdownLoader}>
        <Spin indicator={<LoadingOutlined spin />} />
      </Flex>
    )
  } else if (breakdownData) {
    breakdownContent = (
      <>
        <Title level={5} className={styles.sectionTitle}>
          {t("bookings.cancel_modal.breakdown_title")}
        </Title>
        <Flex vertical gap={12} className={styles.breakdownSection}>
          <Flex justify="space-between" align="center" className={styles.breakdownRow}>
            <Text>{t("bookings.cancel_modal.total_booking_amount")}</Text>
            <Text strong>{numberToCurrency(breakdownData.total_price || 0)}</Text>
          </Flex>
          <Flex justify="space-between" align="center" className={styles.breakdownRow}>
            <Text>{t("bookings.cancel_modal.amount_paid")}</Text>
            <Text strong>{numberToCurrency(breakdownData.amount_paid_by_guest || 0)}</Text>
          </Flex>
          {breakdownData.amount_refunded > 0 && (
            <Flex justify="space-between" align="center" className={styles.breakdownRow}>
              <Text>{t("bookings.cancel_modal.amount_refunded")}</Text>
              <Text strong>{numberToCurrency(breakdownData.amount_refunded)}</Text>
            </Flex>
          )}
          <Flex justify="space-between" align="center" className={styles.breakdownRow}>
            <Flex align="center" gap={6}>
              <Text>{t("bookings.cancel_modal.taxes_and_fees")}</Text>
              {(breakdownData?.tax_and_fee > 0 || breakdownData?.custom_fees?.length > 0) && (
                <Tooltip title={taxesTooltipContent} placement="right">
                  <InfoCircleOutlined className={styles.infoIcon} />
                </Tooltip>
              )}
            </Flex>
            <Text strong>{numberToCurrency(totalTaxes)}</Text>
          </Flex>
          <Divider className={styles.breakdownDivider} />
          <Flex justify="space-between" align="center" className={`${styles.breakdownRow} ${styles.refundableRow}`}>
            <Text strong>{t("bookings.cancel_modal.refundable_amount")}</Text>
            <Text strong className={styles.refundableAmount}>
              {numberToCurrency(breakdownData.refundable_amount || 0)}
            </Text>
          </Flex>
        </Flex>
        <Flex vertical gap={12} className={styles.refundOptions}>
          <Radio.Group value={refundType} onChange={handleRadioChange}>
            <Flex vertical gap={16}>
              <Flex vertical gap={6}>
                <Radio value="full">
                  <Text strong>{t("bookings.cancel_modal.full_refund_label")}</Text>
                </Radio>
                {refundType === "full" && (
                  <Text type="secondary" className={styles.radioNote}>
                    {t("bookings.cancel_modal.full_refund_note")}
                  </Text>
                )}
              </Flex>
              <Flex vertical gap={8}>
                <Radio value="partial">
                  <Text strong>{t("bookings.cancel_modal.partial_refund_label")}</Text>
                </Radio>
                {refundType === "partial" && (
                  <Flex vertical gap={4} className={styles.partialInputWrapper}>
                    <Input
                      type="number"
                      placeholder={t("bookings.cancel_modal.partial_amount_placeholder")}
                      value={partialAmount}
                      onChange={handlePartialAmountChange}
                      prefix="$"
                      min={0}
                      status={partialAmountError ? "error" : ""}
                    />
                    {partialAmountError && <Text className={styles.errorText}>{partialAmountError}</Text>}
                  </Flex>
                )}
              </Flex>
              <Flex vertical gap={6}>
                <Radio value="no_refund">
                  <Text strong>{t("bookings.cancel_modal.no_refund_label")}</Text>
                </Radio>
                {refundType === "no_refund" && (
                  <Text type="secondary" className={styles.radioNote}>
                    {t("bookings.cancel_modal.no_refund_note")}
                  </Text>
                )}
              </Flex>
            </Flex>
          </Radio.Group>
        </Flex>
      </>
    )
  } else {
    breakdownContent = (
      <Text className={styles.errorText}>{t("bookings.cancel_modal.breakdown_load_error")}</Text>
    )
  }

  return (
    <Modal open={open} onCancel={onCancel} footer={null} width={560} title={t("bookings.cancel_modal.title")} destroyOnClose centered maskClosable={false}>
      <div className={styles.cancelBookingModalContent}>
        {/* Booking card */}
        <Flex align="center" gap={16} className={styles.bookingCard}>
          {booking?.image && <img src={booking.image} alt="trip" className={styles.bookingImage} />}
          <Flex vertical gap={4}>
            {booking?.title && <Text strong>{booking.title}</Text>}
            {booking?.booking_id && <Text type="secondary">{t("bookings.cancel_modal.booking_id", { id: booking.booking_id })}</Text>}
            {booking?.booked_on && <Text type="secondary">{t("bookings.cancel_modal.booked_on", { date: booking.booked_on })}</Text>}
          </Flex>
        </Flex>

        <Divider className={styles.sectionDivider} />

        {/* Cancellation reason */}
        <Flex vertical gap={6} className={styles.reasonSection}>
          <Text strong>{t("bookings.cancel_modal.reason_title")}</Text>
          {reasonsFailed ? (
            <Text className={styles.errorText}>{t("bookings.cancel_modal.reason_load_error")}</Text>
          ) : (
            <>
              <Select
                placeholder={t("bookings.cancel_modal.reason_placeholder")}
                value={selectedReason}
                loading={reasonsLoading}
                disabled={reasonsLoading}
                onChange={(value) => {
                  setSelectedReason(value)
                  setOtherReason("")
                  setOtherReasonError("")
                  if (reasonError) setReasonError("")
                }}
                options={(cancelReasons || []).map((r) => ({ value: r.id, label: r.reason }))}
                status={reasonError ? "error" : ""}
                style={{ width: "100%" }}
              />
              {reasonError && <Text className={styles.errorText}>{reasonError}</Text>}
              {isOtherSelected && (
                <Flex vertical gap={4}>
                  <Input.TextArea
                    placeholder={t("bookings.cancel_modal.other_reason_placeholder")}
                    value={otherReason}
                    onChange={(e) => {
                      setOtherReason(e.target.value)
                      if (otherReasonError) setOtherReasonError("")
                    }}
                    rows={3}
                    status={otherReasonError ? "error" : ""}
                  />
                  {otherReasonError && <Text className={styles.errorText}>{otherReasonError}</Text>}
                </Flex>
              )}
            </>
          )}
        </Flex>

        <Divider className={styles.sectionDivider} />

        {/* Payment breakdown */}
        {breakdownContent}
      </div>

      <Flex gap={12} className={styles.modalFooter}>
        <Button variant="outlined" color="primary" onClick={onCancel} disabled={submitLoading}>
          {t("bookings.cancel_modal.cancel_button")}
        </Button>
        <Button
          color="danger"
          variant="solid"
          onClick={handleConfirmClick}
          disabled={submitLoading || breakdownLoading}
          loading={submitLoading}
        >
          {t("bookings.cancel_modal.confirm_button")}
        </Button>
      </Flex>

      {submitLoading && (
        <Flex justify="center" align="center" className={`position-absolute ${styles.modalLoaderWrapper}`}>
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </Flex>
      )}
    </Modal>
  )
}

CancelBookingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      uuid: PropTypes.string,
      image: PropTypes.string,
      title: PropTypes.string,
      booking_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      booked_on: PropTypes.string
    })
  ).isRequired
}

export default CancelBookingModal
 